import React,{useRef} from 'react';
import { Armchair as Wheelchair, Bed, Sprout, GraduationCap } from 'lucide-react';
import { useCinematicReveal } from '../hooks/useCinematicReveal';
const services = [
  {
    icon: Bed,
    title: 'Medical Bed Support',
    desc:
      'Hospital-grade beds, medical equipment.',
    items: [
      'Adjustable hospital beds',
      'Medical mattresses',
      'Home nursing equipment',
      '24/7 technical support',
    ],
  },
  {
    icon: Sprout,
    title: 'Agricultural Support',
    desc:
      'Empowering small-scale farmers through resources, training, and financial assistance.',
    items: [
      'Seeds & modern equipment',
      'Advanced farming training',
      'Micro-finance assistance',
      'Market access programs',
    ],
  },
  {
    icon: Wheelchair,
    title: 'Mobility Assistance',
    desc:
      'Providing mobility aids to enhance independence and dignity for persons with disabilities.',
    items: [
      'Manual wheelchairs',
      'Walking aids & crutches',
      'Physiotherapy equipment',
      'Accessibility upgrades',
    ],
  },
  {
    icon: GraduationCap,
    title: 'Educational Support',
    desc:
      'Academic assistance and guidance to help students overcome challenges and succeed.',
    items: [
      'Backlog subject tutoring',
      'Books & learning materials',
      'Scholarship programs',
      'Career counseling',
    ],
  },
];

const Services = () => {
  const sectionRef = useRef(null);
  useCinematicReveal(sectionRef);
  return (
  <section
    id="services"
    ref={sectionRef}
    className="relative py-40 -mt-32
        bg-gradient-to-b from-white/30 via-white/10 to-white/30
        backdrop-blur-xl"
  >
    <div className="max-w-8xl mx-auto px-12 sm:px-8 lg:px-14">

      {/* Section Header */}
      <div className="text-center mb-24 space-y-6">
        <span className="inline-block text-sm tracking-widest text-blue-600 uppercase">
          What We Do
        </span>
        <h2 className="text-4xl md:text-5xl font-bold text-blue-900 leading-tight">
          Our Comprehensive Services
        </h2>
        <p className="text-xl text-blue-800 max-w-4xl mx-auto leading-relaxed">
          We deliver holistic, community-driven support across healthcare,
          agriculture, mobility, and education ensuring dignity, opportunity,
          and hope for all.
        </p>
      </div>

      {/* Services Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-14 mb-32">
        {services.map(({ icon: Icon, title, desc, items }) => (
          <div
            key={title}
            className="group relative rounded-3xl bg-white/50 backdrop-blur-xl border border-white/30
                       p-10 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.2)]
                       transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_30px_80px_-20px_rgba(37,99,235,0.35)]"
          >
            {/* Icon */}
            <div className="mb-10 inline-flex items-center justify-center w-16 h-16 rounded-2xl
                            bg-gradient-to-br from-blue-600 to-blue-500
                            shadow-lg group-hover:scale-110 transition-transform">
              <Icon className="w-8 h-8 text-white" />
            </div>

            {/* Content */}
            <h3 className="text-xl font-semibold text-blue-900 mb-4">
              {title}
            </h3>

            <p className="text-blue-800 leading-relaxed mb-8">
              {desc}
            </p>

            <ul className="space-y-3 text-sm text-blue-700">
              {items.map(item => (
                <li key={item} className="flex gap-2">
                  <span className="text-blue-500">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Additional Services */}
      <div className="relative rounded-3xl bg-white/50 backdrop-blur-xl border border-white/30 p-14 shadow-xl">
        <h3 className="text-4xl font-bold text-blue-900 text-center mb-14">
          Additional Support Services
        </h3>

        <div className="grid md:grid-cols-3 gap-14 text-center">
          {[
            {
              title: 'Emergency Relief',
              desc: 'Rapid response assistance during natural disasters and crisis situations.',
            },
            {
              title: 'Community Development',
              desc: 'Infrastructure, empowerment, and long-term community uplift programs.',
            },
            {
              title: 'Mental Health Support',
              desc: 'Professional counseling and psychological well-being initiatives.',
            },
          ].map(item => (
            <div key={item.title} className="space-y-4">
              <h4 className="text-lg font-semibold text-blue-900">
                {item.title}
              </h4>
              <p className="text-blue-800 text-sm leading-relaxed max-w-xs mx-auto">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </div>

    </div>
  </section>
);
};

export default Services;
